import { useCallback, useEffect, useState } from 'react';
import { TriggeredBy } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { motion } from 'motion/react';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { InboxAiStatus } from '@frontend/inbox-ai-status';
import { ThreadBodyHooks } from '@frontend/thread-body';
import { theme } from '@frontend/theme';
import { Dot, NakedButton, SwitchField, Text, useAlert, useFormField } from '@frontend/design-system';
import { aiAssistantStyles } from '../components/header-components/ai-assistant-button';
import { buttonStyles } from './thread-options-drawer';

type Props = {
  groupId: string;
  threadId: string;
  locationPhone: string;
  /** True when thread is calculated but doesn't exist yet (new conversation) */
  isNew?: boolean;
};

export const AiDrawerItem = ({ groupId, threadId, locationPhone, isNew }: Props) => {
  const { t } = useTranslation('mini-chat');
  const alert = useAlert();

  // Get the unified thread updater to update the cache when bot status changes
  const unifiedThreadUpdater = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();

  const { getThreadQuery } = ThreadBodyHooks.useGetThreadData({
    threadId: threadId,
    locationId: groupId,
    isNew,
  });

  const thread = getThreadQuery?.data?.pages?.[0]?.thread as Thread;
  const aiStatus = thread?.aiStatus;

  // Compute AI status based on seeded flag and locationPhone
  const effectiveStatus = InboxAiStatus.Hooks.useEffectiveAIStatus({
    aiStatus,
    locationPhone,
    locationId: groupId,
  });

  const [botIsActive, setBotIsActive] = useState<boolean>(effectiveStatus.isActive);

  const aiBotToggle = useFormField(
    {
      type: 'switch',
      value: botIsActive,
    },
    [botIsActive]
  );

  // Helper to update aiStatus in the thread cache
  const updateThreadAiStatusInCache = useCallback(
    (newIsActive: boolean) => {
      unifiedThreadUpdater.upsertUnifiedThread({
        matchValues: {
          threadId,
          locationId: groupId,
        },
        updater: (existingThread) => {
          if (!existingThread) return undefined;
          return {
            ...existingThread,
            aiStatus: {
              ...existingThread.aiStatus,
              isActive: newIsActive,
              isSeeded: true,
            },
          };
        },
        isOptimistic: false,
      });
    },
    [unifiedThreadUpdater, threadId, groupId]
  );

  const setBotActiveMutation = SMSDataV4.Mutations.useSetBotActiveMutation({
    options: {
      onSuccess: () => {
        alert.success(
          t('AI Receptionist is now active in this thread and will continue monitoring this conversation.')
        );
        setBotIsActive(true);
        updateThreadAiStatusInCache(true);
      },
    },
  });

  const setBotInactiveMutation = SMSDataV4.Mutations.useSetBotInactiveMutation({
    options: {
      onSuccess: () => {
        alert.success(
          t('AI Receptionist is no longer active in this thread. It will return automatically in 30 days.')
        );
        setBotIsActive(false);
        updateThreadAiStatusInCache(false);
      },
    },
  });

  const handleToggle = useCallback(() => {
    if (botIsActive) {
      setBotInactiveMutation.mutate({
        locationId: groupId,
        threadId,
        triggeredBy: TriggeredBy.TRIGGERED_BY_WEAVE_USER,
      });
    } else {
      setBotActiveMutation.mutate({
        locationId: groupId,
        threadId,
        triggeredBy: TriggeredBy.TRIGGERED_BY_WEAVE_USER,
      });
    }
  }, [botIsActive, groupId, threadId, setBotActiveMutation, setBotInactiveMutation]);

  // Sync botIsActive with effective status
  useEffect(() => {
    setBotIsActive(effectiveStatus.isActive);
  }, [effectiveStatus.isActive]);

  // Don't show UI if shouldShowUI is false
  if (!effectiveStatus.shouldShowUI) return null;

  return (
    <NakedButton
      css={buttonStyles}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      trackingId='mini-chat-ai-toggle'
    >
      <span
        css={{
          display: 'flex',
          gap: theme.spacing(1),
          alignItems: 'center',
        }}
      >
        <motion.div
          css={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Icon css={aiAssistantStyles} name='aiweave-assistant' size={16} />
          <Dot
            color={botIsActive ? 'success' : 'neutral'}
            css={{
              position: 'static',
              display: 'inline-flex',
              transform: `translateY(0.2em) translateX(-0.2em)`,
            }}
          />
        </motion.div>
        <Text>{t('Ai Receptionist')}</Text>
      </span>
      <span
        css={{
          display: 'flex',
          gap: theme.spacing(1),
          alignItems: 'center',
        }}
      >
        <SwitchField {...aiBotToggle} name='ai-bot-toggle' label='' onChange={handleToggle} />
      </span>
    </NakedButton>
  );
};
